import React, { useState, useRef, useCallback, useMemo } from "react";
import ProductItem from "./ProductItem";
import { IProduct, ICategory, IProductsQuery } from "@/props/IProduct";
import type { PaginationProps } from "antd";
import { useRouter } from "next/router";
import { Pagination, Empty, Spin } from "antd";
import { CaretUpOutlined, CaretDownOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { getProductsApi } from "@/api/product";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/index";
import { useTranslation } from "next-i18next";
import useSWR from "swr";

import type { LayoutMode } from "@/components/Mall/SortFilter";

import dynamic from "next/dynamic";
import { onKeyActivate } from "@/utils/index";
const SortFilter = dynamic(() => import("@/components/Mall/SortFilter"), {
  ssr: false,
});
const DreamValueFilter = dynamic(() => import("@/components/Mall/DreamValueFilter"), { ssr: false });
const CategoryFilter = dynamic(() => import("@/components/Mall/CategoryFilter"), { ssr: false });
const BrandFilter = dynamic(() => import("@/components/Mall/BrandFilter"), {
  ssr: false,
});

/**
 * fetcher: get product list from API
 */
const fetcher = async (params: IProductsQuery & { keyword?: string }) => {
  const data: any = await getProductsApi(params);
  data.list.forEach((item: IProduct) => {
    item.storeName = item.storeName ? JSON.parse(item.storeName) : {};
    item.image = item.image ? JSON.parse(item.image) : {};
    item.storeInfo = item.storeInfo ? JSON.parse(item.storeInfo) : {};
    item.categories.forEach((category: ICategory) => {
      category.name = JSON.parse(category.name);
    });
  });
  return data;
};

/**
 * PC products list
 */
/**
 * 可折叠筛选区块
 */
const CollapsibleSection = ({
  title,
  count,
  defaultOpen = true,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="flex w-full flex-col border-b border-gray-100 last:border-b-0 lg:py-4 xl:py-6">
      <div
        className="flex cursor-pointer items-center justify-between transition-colors hover:text-primary"
        onClick={() => setOpen((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={onKeyActivate(() => setOpen((prev) => !prev))}
      >
        <div className="flex items-center gap-2 text-base font-semibold">
          <span>{title}</span>
          {count != null && count > 0 && (
            <span className="flex h-4 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-white">
              {count}
            </span>
          )}
        </div>
        {open ? (
          <CaretUpOutlined className="text-base text-stone-400" />
        ) : (
          <CaretDownOutlined className="text-base text-stone-400" />
        )}
      </div>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
};

const PcProductsList = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isMobile } = useAppSelector((state: RootState) => state.app);

  const [queryParams, setQueryParams] = useState<IProductsQuery>({
    page: 1,
    limit: 15,
    priceOrder: "asc",
    shelfOrder: "",
    brandId: "",
    cid: "",
  });

  const [layoutMode, setLayoutMode] = useState<LayoutMode>("grid");

  // 用于强制重置子组件内部状态
  const [resetKey, setResetKey] = useState(0);

  const [categories, setCategories] = useState<ICategory[]>();
  const keyword = typeof router.query.keyword === "string" ? router.query.keyword : "";
  const swrKey = isMobile ? null : ["products", { ...queryParams, keyword }];

  const { data, isLoading, error } = useSWR(
    swrKey,
    ([, params]) => fetcher(params as IProductsQuery & { keyword?: string }),
    { revalidateOnFocus: false }
  );

  // 计算已选数量
  const categoryCount = useMemo(() => {
    if (!queryParams.cid || queryParams.cid === "") return 0;
    return queryParams.cid.split(",").filter(Boolean).length;
  }, [queryParams.cid]);

  const brandCount = useMemo(() => {
    if (!queryParams.brandId || queryParams.brandId === "") return 0;
    return queryParams.brandId.split(",").filter(Boolean).length;
  }, [queryParams.brandId]);

  const hasAnyFilter = useMemo(() => {
    return categoryCount > 0 || brandCount > 0 || queryParams.minPrice != null || queryParams.maxPrice != null;
  }, [categoryCount, brandCount, queryParams.minPrice, queryParams.maxPrice]);

  // handle page change
  const onPageChange: PaginationProps["onChange"] = (page) => {
    setQueryParams((prev) => ({ ...prev, page }));
  };

  const onSortChange = (activeIndex: number, value: string) => {
    setQueryParams((prev) => ({
      ...prev,
      priceOrder: activeIndex === 0 ? value : "",
      shelfOrder: activeIndex !== 0 ? value : "",
    }));
  };

  const onPriceChange = (priceArr: (number | null)[]) => {
    setQueryParams((prev) => ({
      ...prev,
      minPrice: priceArr[0] ?? undefined,
      maxPrice: priceArr[1] ?? undefined,
    }));
  };

  const onCategoryChange = (cid: string) => {
    setQueryParams((prev) => ({ ...prev, cid }));
  };

  const onBrandChange = (brandId: string) => {
    setQueryParams((prev) => ({ ...prev, brandId }));
  };

  // 清除所有筛选
  const handleClearAll = useCallback(async () => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      cid: "",
      brandId: "",
      minPrice: undefined,
      maxPrice: undefined,
    }));
    // 先清除 URL 上的 cid 参数，等完成后再 remount 子组件
    if (router.query.cid) {
      const { cid, ...rest } = router.query;
      await router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true });
    }
    setResetKey((prev) => prev + 1);
  }, [router]);

  // pc filters block
  const PcFilterContent = (
    <>
      {/* 清除所有筛选 */}
      {hasAnyFilter && (
        <div
          className="my-2 flex cursor-pointer items-left justify-start gap-1 rounded border-gray-200 py-2 text-sm text-stone-500 transition-colors hover:text-primary"
          onClick={handleClearAll}
        >
          <CloseCircleOutlined className="text-xs" />
          {t("product_category.clear_all_filters")}
        </div>
      )}
      <CollapsibleSection title={t("common.dream_value")}>
        <DreamValueFilter key={`dv-${resetKey}`} onChange={onPriceChange} hideTitle />
      </CollapsibleSection>
      <CollapsibleSection title={t("product_category.product_category_title")} count={categoryCount}>
        <CategoryFilter key={`cat-${resetKey}`} onInit={setCategories} onChange={onCategoryChange} hideTitle />
      </CollapsibleSection>
      <CollapsibleSection title={t("product_category.brand_category")} count={brandCount}>
        <BrandFilter key={`brand-${resetKey}`} onChange={onBrandChange} hideTitle />
      </CollapsibleSection>
    </>
  );

  return (
    <div className="mt-2 hidden w-full justify-between px-mobile pb-20 lg:mt-6 lg:flex lg:w-md-container lg:px-0 lg:pb-20 xl:w-container">
      <div
        className="hidden lg:mr-5 lg:block xl:mr-7"
        style={{
          position: "sticky",
          top: "110px",
          alignSelf: "flex-start",
          height: "calc(100vh - 130px)",
        }}
      >
        <div className="flex h-full flex-col overflow-y-auto rounded-lg border border-gray-100 px-5 pb-4 lg:w-[250px] xl:w-[270px]">
          {PcFilterContent}
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        <SortFilter onChange={onSortChange} layoutMode={layoutMode} onLayoutChange={setLayoutMode} />
        {isLoading || (data?.list?.length ?? 0) > 0 ? (
          <>
            {/* 上面这个结构 */}
            <div className="relative mb-10 flex min-h-screen w-full flex-col items-center lg:mb-20">
              <div
                className={
                  layoutMode === "list"
                    ? "flex w-full flex-col gap-4"
                    : "grid w-full grid-cols-2 gap-mobile md:grid-cols-3 lg:gap-4 xl:gap-30px"
                }
              >
                {(isLoading ? Array(queryParams.limit).fill(0) : data?.list || []).map((item, i) =>
                  isLoading ? (
                    <ProductItem key={`skeleton-${i}`} loading layout={layoutMode} />
                  ) : (
                    <ProductItem key={item.id} product={item} layout={layoutMode} showFavoriteIcon/>
                  )
                )}
              </div>

              {isLoading && (
                <div className="absolute left-1/2 top-20 z-10 -translate-x-1/2">
                  <Spin />
                </div>
              )}
            </div>

            {/* 分页，仅当非 loading 且有数据时展示 */}
            {!isLoading && data?.total > 0 && (
              <div className="mb-28 flex justify-center lg:mb-20">
                <Pagination
                  showSizeChanger={false}
                  className="mall-pagination"
                  current={queryParams.page}
                  pageSize={queryParams.limit}
                  onChange={onPageChange}
                  total={data.total}
                />
              </div>
            )}
          </>
        ) : (
          // 无数据状态
          <div className="mt-20">
            <Empty description={t("common.no_data")} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PcProductsList;
